/*
 * utm tags on every invite (referral) link the web app shares.
 *
 * A friend who signs up from an invite is labelled by the first touch the
 * attribution store keeps (utils/attribution.js), which the X-QLearn-Entry
 * header carries to new_account_entries and the growth_receipt entry line
 * (utils/entry-header.js). With only ?ref= on the link, those sign-ups read as
 * "no utm". So every shared link gets:
 *   utm_source=qlearn_invite  utm_medium=referral  utm_campaign=<surface>
 * and, for GA4 and the stored touch only (the entry header reads just the
 * three above), utm_content=<channel>.
 *
 * The surface is where the share window was opened from; the channel is the
 * button used in it. The friend can read them in the link, so they stay
 * neutral (no "paywall", no "limit"). Both are fixed slugs from the lists below, never the
 * referral code, a name or anything else about the person sharing: a value
 * outside the lists becomes INVITE_CAMPAIGN.UNSPECIFIED, or no utm_content.
 * Each slug is lower-case words joined by "_" with no digits, so it passes the
 * entry header's [A-Za-z0-9_.-] rule and the server's 16+ letters-and-digits
 * run check (QLearn-service NewAccountEntryReceipt).
 *
 * `ref` and every other pair already on the link are kept exactly as they
 * are. A utm key the link already carries (the server tags its own links with
 * campaign invite_link, ReferralService.tagAsInvite) is replaced, never
 * repeated; a utm_term or utm_id it carries is dropped, so no part of an older
 * tag set rides along with the invite's.
 *
 * Web only, like every invite surface: the native app shows no invite (iOS
 * ruling B5, utils/native-referral-policy.js), so nothing there calls this.
 */

export const INVITE_UTM_SOURCE = 'qlearn_invite';
export const INVITE_UTM_MEDIUM = 'referral';

export const INVITE_CAMPAIGN = Object.freeze({
  // ReferralSharePrompt, by its `surface` prop (see PROMPT_SURFACE_CAMPAIGNS).
  RESULT_PROMPT: 'invite_result_prompt',
  DIAGNOSTIC_PROMPT: 'invite_diagnostic_prompt',
  PLANS_PROMPT: 'invite_plans_prompt',
  // The menu's "Share & Earn" item (side drawer and the bottom tabs' More sheet).
  SHARE_EARN_MENU: 'invite_share_earn',
  // Create Quiz's out-of-questions window, "Invite".
  CREATE_QUIZ: 'invite_create_quiz',
  // The QLink page's share button.
  QLINK_PAGE: 'invite_qlink_page',
  // The institutional campaign's own link, from whichever opener: the server
  // tags it so, and ShareEarnModal keeps it, so the campaign's sign-ups stay
  // apart from ordinary invites in the entry record.
  INSTITUTIONAL: 'invite_institutional',
  // An opener that names no surface. Also the server's own campaign.
  UNSPECIFIED: 'invite_link',
});

export const INVITE_CHANNEL = Object.freeze({
  COPY: 'copy_link',
  NATIVE: 'native_share',
  WHATSAPP: 'whatsapp',
  FACEBOOK: 'facebook',
  TWITTER: 'twitter',
});

const PROMPT_SURFACE_CAMPAIGNS = Object.freeze({
  exam_result: INVITE_CAMPAIGN.RESULT_PROMPT,
  diagnostic_report: INVITE_CAMPAIGN.DIAGNOSTIC_PROMPT,
  paywall: INVITE_CAMPAIGN.PLANS_PROMPT,
});

const CAMPAIGNS = new Set(Object.values(INVITE_CAMPAIGN));
const CHANNELS = new Set(Object.values(INVITE_CHANNEL));
const REPLACED_KEYS = new Set(['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id']);

/** The campaign for a ReferralSharePrompt surface ('exam_result', …). */
export const promptInviteCampaign = (surface) => (
  Object.prototype.hasOwnProperty.call(PROMPT_SURFACE_CAMPAIGNS, surface)
    ? PROMPT_SURFACE_CAMPAIGNS[surface]
    : INVITE_CAMPAIGN.UNSPECIFIED
);

const keyOf = (pair) => {
  const raw = pair.split('=', 1)[0];
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' '));
  } catch {
    return raw;
  }
};

/**
 * `link` with the invite utm tags, or `link` itself when it is not a
 * non-empty string. Never throws: it runs inside the share buttons.
 */
export const withInviteUtm = (link, { campaign, channel } = {}) => {
  if (typeof link !== 'string' || !link) return link;

  const hashAt = link.indexOf('#');
  const fragment = hashAt >= 0 ? link.slice(hashAt) : '';
  const beforeFragment = hashAt >= 0 ? link.slice(0, hashAt) : link;
  const queryAt = beforeFragment.indexOf('?');
  const base = queryAt >= 0 ? beforeFragment.slice(0, queryAt) : beforeFragment;
  const query = queryAt >= 0 ? beforeFragment.slice(queryAt + 1) : '';

  const pairs = query.split('&').filter((pair) => pair && !REPLACED_KEYS.has(keyOf(pair)));
  pairs.push(
    `utm_source=${INVITE_UTM_SOURCE}`,
    `utm_medium=${INVITE_UTM_MEDIUM}`,
    `utm_campaign=${CAMPAIGNS.has(campaign) ? campaign : INVITE_CAMPAIGN.UNSPECIFIED}`,
  );
  if (CHANNELS.has(channel)) {
    pairs.push(`utm_content=${channel}`);
  }
  return `${base}?${pairs.join('&')}${fragment}`;
};

/**
 * The link as the share window's chip shows it: no scheme and no utm pairs,
 * so a phone shows one readable line ("qlearn.medlearnity.com/login?ref=…")
 * instead of four lines of tags. The copy button and every share button send
 * the tagged link, and so does a copy made by hand from the chip (the chip's
 * copy handler). `link` itself when it is not a non-empty string.
 */
export const inviteDisplayLink = (link) => {
  if (typeof link !== 'string' || !link) return link;
  const beforeFragment = link.split('#', 1)[0];
  const queryAt = beforeFragment.indexOf('?');
  const base = queryAt >= 0 ? beforeFragment.slice(0, queryAt) : beforeFragment;
  const kept = (queryAt >= 0 ? beforeFragment.slice(queryAt + 1) : '')
    .split('&')
    .filter((pair) => pair && !REPLACED_KEYS.has(keyOf(pair)));
  const keptQuery = kept.length ? `?${kept.join('&')}` : '';
  return `${base}${keptQuery}`.replace(/^https?:\/\//i, '');
};
